import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate L2-5 dello Sciamano (manuale V1, rr. 5556-5680).
 *
 * 16 mosse di tipo `advanced_2_5`. Pattern stress-testati:
 *
 * - ***Che Tu Sia...*** (D1): mossa-config con `choices: [benevolenza,
 *   malevolenza]`, `choiceCount: 1`. La scelta avviene al momento della presa
 *   (manuale r. 5573). Pattern coerente con *Faro di Devozione* (Paladino, 7
 *   Aspetti), *Sentieri del Monaco* (7 restrizioni), *Aura* (Paladino, 5 scopi),
 *   *Nato dalla Terra* (Druido, 10 Terre), *Ordine Sacro* (Paladino, 3
 *   Tradizioni). Il sistema costo-condizioni-livello concordato col GM resta
 *   in description (FI-18 strutturerà eventualmente).
 *
 * - ***Bambole e Pendoli*** (D2): description-only, `prerequisites: []`. Pattern
 *   stabilito a 4 occorrenze precedenti — *Allargare le Basi* (Chierico),
 *   *Mille Risorse* (Ladro), *Di Tutto un Po'* (Bardo), *Formazione Eterogenea*
 *   (Mago). È un tema di avanzamento livello, gestito a runtime al level-up.
 *
 * - **FI-8 stub description-only** (D3): *Animare Zombi* e *Collezionista di
 *   Teschi* sono pseudo-gregari (zombi gregario L2; teschio con prese e mosse
 *   associate). Refactor unificato Ranghi 1-3 nella PR FI-8 post-12 classi.
 *   Entrambe hanno `prerequisites: ['sciamano.aiuto-dall-altro-mondo']`
 *   strutturato per facilitare il refactor.
 *
 * - **Mossa multiclasse** (FI-17): *Amico degli Spiriti* sceglie da Barbaro o
 *   Druido (extension all'archetipo Elfo: anche Mago). Description-only.
 *
 * - **Cross-class ref testuale**: *Guarigione Spirituale* cita *Guarire l'Anima*
 *   del Chierico in description (i due effetti non si cumulano). Description-only.
 */

const bambolePendoli: Move = {
  id: 'sciamano.bambole-e-pendoli',
  name: 'Bambole e Pendoli',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Scegli una mossa iniziale dello Sciamano che non hai già preso.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede una mossa iniziale residua del gruppo dello Sciamano.
  grantsMoveChoice: { kind: 'from-group', groupId: 'mossa-iniziale-aggiuntiva' },
};

const ladroDOcchi: Move = {
  id: 'sciamano.ladro-d-occhi',
  name: "Ladro d'Occhi",
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger:
    "Quando una creatura che hai accecato, assordato o reso muta recupera l'uso della vista, dell'udito o della parola",
  description:
    "Puoi spendere 1 presa di *Aiuto dall'Altro Mondo* per far uso del senso o della capacità che essa ha recuperato. L'effetto dura fino a quando riesci a mantenere la concentrazione.",
  tags: [],
  prerequisites: ['sciamano.aiuto-dall-altro-mondo'],
  replaces: [],
};

// Sistema costo-condizioni-livello concordato col GM resta in description
// (pattern coerente con *Plasmare gli Elementi* del Druido). FI-18
// strutturerà eventualmente i sotto-gruppi di scelta.
const cheTuSia: Move = {
  id: 'sciamano.che-tu-sia',
  name: 'Che Tu Sia...',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger:
    'Al momento della presa: scegli benevolenza o malevolenza. A invocazione: quando dedichi qualche minuto a benedire o maledire una creatura',
  description:
    "Quando **prendi questa mossa**, decidi se le tue invocazioni si basano sulla *benevolenza* o sulla *malevolenza* dell'aldilà.\n\nQuando **dedichi qualche minuto a benedire o a maledire una creatura** (in base al tipo di invocazioni che hai scelto), spiega l'effetto che desideri (e i suoi limiti) e concordane con il GM il livello (0, 1, 3, 5, 7 o 9) prendendo spunto dagli incantesimi esistenti. Se il tuo livello da Sciamano è inferiore a quello concordato, *non* ottieni l'effetto. Altrimenti, scegli dalla lista un numero di condizioni pari a 1 più la metà del livello stabilito (arrotondando per eccesso). Puoi scegliere la stessa opzione più volte, salvo che non sia indicato diversamente.\n\nSe l'effetto è *continuato*, devi scegliere *almeno una* delle seguenti condizioni:\n\n- subisci un -1 continuato ai tiri di *Aiuto dall'Altro Mondo* fino all'alba successiva al termine dell'effetto;\n- subisci 4 danni (*ignora armatura*) e non recuperi PF finché l'effetto permane (puoi scegliere questa opzione una sola volta);\n- vieni maledetto (perdi l'uso della vista, emani un fetore terribile, uno spirito ti sussurra all'orecchio, o cose simili). Il GM ti dirà in che modo, ma solo dopo che l'effetto si sarà attivato! Ogni volta che scegli questa opzione subisci una maledizione aggiuntiva. Le maledizioni durano per qualche ora dopo il termine dell'effetto.\n\nScegli le altre opzioni (*tutte* se l'effetto *non è continuato*) tra queste:\n\n- subisci una Debilitazione a tua scelta (puoi scegliere questa opzione una volta per ogni Debilitazione che non hai segnato);\n- impieghi alcune ore (giorni, se scegli questa opzione due volte);\n- indebolisci il velo che separa l'aldilà da questo mondo (il GM stabilirà con quali conseguenze);\n- ti fai aiutare da uno spirito dell'aldilà (descrivi quale, e il GM ti dirà cosa vuole in cambio);\n- usi un raro ingrediente, un feticcio appositamente preparato o qualcosa cui il bersaglio è fortemente legato (descrivi cosa).\n\nSe il GM approva le tue scelte, ottieni l'effetto desiderato. Con questa mossa *non* puoi ottenere effetti permanenti.\n\nQuando **benedici o maledici “al volo” qualcuno che vedi**, ti basta qualche secondo. Stabilisci come al solito livello e condizioni, e poi tira+**CAR** (applicando i modificatori al tiro che hai per *Aiuto dall'Altro Mondo*). Con 10+, tutto va come previsto. Con 7-9, il GM ti dirà un effetto collaterale, una strana limitazione, o le ripercussioni che l'invocazione ha avuto nell'aldilà.",
  tags: [],
  prerequisites: ['sciamano.aiuto-dall-altro-mondo'],
  replaces: [],
  choices: [
    {
      id: 'benevolenza',
      label: 'Benevolenza',
      description: "Le tue invocazioni si basano sulla benevolenza dell'aldilà (benedizioni).",
    },
    {
      id: 'malevolenza',
      label: 'Malevolenza',
      description: "Le tue invocazioni si basano sulla malevolenza dell'aldilà (maledizioni).",
    },
  ],
  choiceCount: 1,
};

const spiritiGenerosi: Move = {
  id: 'sciamano.spiriti-generosi',
  name: 'Spiriti Generosi',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: "Quando guadagni prese con *Aiuto dall'Altro Mondo*",
  description: 'Con 7+, ottieni +1 presa.',
  tags: [],
  prerequisites: ['sciamano.aiuto-dall-altro-mondo'],
  replaces: [],
};

const animareZombi: Move = {
  id: 'sciamano.animare-zombi',
  name: 'Animare Zombi',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: 'Quando invochi uno spirito affinché prenda possesso di un cadavere fresco',
  description:
    "Spendi una presa di *Aiuto dall'Altro Mondo* e tira+**CAR** (applicando i modificatori al tiro che hai per *Aiuto dall'Altro Mondo*). Con 7+, crei uno zombi che esegue *senza discutere* i tuoi ordini al meglio delle sue limitate possibilità. Con 10+, prendi tutte le peculiarità; con 7-9, scegli 2.\n\nLo zombi si cura *solo* bevendo il tuo sangue, e recupera un numero di PF pari al danno (*ignora armatura*) che accetti di subire. Quando viene curato in questo modo, fino all'alba successiva non sembrerà più un cadavere. Puoi tenere “in vita” **un solo zombi per volta**.",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Crei lo zombi e prendi tutte le 4 capacità extra.' },
    on7to9: { text: 'Crei lo zombi e scegli 2 capacità extra dalla lista.' },
  },
  tags: [],
  prerequisites: ['sciamano.aiuto-dall-altro-mondo'],
  replaces: [],
  // D8a — FI-8d: mossa-source di Follower (Animare Zombi).
  followerTemplateId: 'sciamano-animare-zombi',
  // pickCount statico = 4 (best case 10+ = tutte). Sovrascritto runtime via
  // `MoveRef.recruitOutcome` → `getPickCountForOutcome` (D8a).
  choices: [
    {
      id: 'mod-piu-2',
      label: 'Mod +2',
      description: 'Un modificatore di Caratteristica a +2 invece di +1.',
    },
    {
      id: 'resistente',
      label: 'Resistente',
      description: '+2 PF per ogni tuo livello (al massimo +20 PF).',
    },
    {
      id: 'cervello-funzionante',
      label: 'Cervello funzionante',
      description: 'Esegue ordini complessi (non solo gesti elementari).',
    },
    {
      id: 'non-sembra-cadavere',
      label: 'Non sembra un cadavere',
      description: 'Per un paio di giorni può passare per vivo agli occhi più disattenti.',
    },
  ],
  narrativeFields: [
    {
      id: 'nome-zombi',
      label: 'Nome dello zombi',
      placeholder: 'es. Stoffa-d-Ombra, Marrocco, Ezzelina…',
      required: true,
      multiline: false,
    },
    {
      id: 'quale-mod-zombi',
      label: 'Modificatore "mod +2"',
      placeholder: 'Quale stat ottiene il bonus?',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'main', optionId: 'mod-piu-2' },
      oneOf: ['forza', 'destrezza', 'costituzione', 'intelligenza', 'saggezza', 'carisma'],
    },
  ],
};

// FI-8 stub: il teschio è uno pseudo-gregario description-only (oggetto
// persistente con prese, utilizzi e mosse associate concesse dal GM).
// Refactor unificato nella PR FI-8 post-12 classi.
const collezionistaDiTeschi: Move = {
  id: 'sciamano.collezionista-di-teschi',
  name: 'Collezionista di Teschi',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger:
    'Quando entri in possesso del teschio di una creatura (peso 1) e quando hai la tranquillità e il tempo necessari per vincolarlo',
  description:
    "Quando **entri in possesso del teschio di una creatura** (peso 1), il GM ti dice se il suo spirito è ancora raggiungibile e ti rivela una o più mosse a esso associate (con innesco «*Quando **usi il teschio**, ...*» e senza necessità di un tiro per ottenere l'effetto).\n\nQuando **hai la tranquillità e il tempo necessari**, puoi provare a vincolare lo spirito al teschio. Tira+**CAR** (applicando i modificatori al tiro che hai per *Aiuto dall'Altro Mondo*). Con 10+, il teschio ha 3 utilizzi. Con 7-9, solo uno.\n\nPuoi attivare una mossa associata allo spirito spendendo 1 presa di *Aiuto dall'Altro Mondo* e 1 utilizzo del teschio.",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Il teschio ha 3 utilizzi.' },
    on7to9: { text: 'Il teschio ha 1 utilizzo.' },
  },
  tags: [],
  prerequisites: ['sciamano.aiuto-dall-altro-mondo'],
  replaces: [],
};

const canzoneDelMioPopolo: Move = {
  id: 'sciamano.la-canzone-del-mio-popolo',
  name: 'La Canzone del Mio Popolo',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: 'Quando canti una canzone sacra, declami un poema o ti esibisci in una danza',
  description:
    'Tira+**SAG**. Con 7-9, gli astanti sono commossi dalla tua esibizione e saranno inclini ad aiutarti. Con 10+, sentiranno anche il bisogno di offrirti una qualche forma di compenso. Con un fallimento, aspettati a breve la visita di uno dei tuoi antenati per chiederti come mai hai condiviso i riti sacri con degli estranei.',
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Astanti commossi + sentono il bisogno di offrirti compenso.' },
    on7to9: { text: 'Astanti commossi e inclini ad aiutarti.' },
    on6minus: { text: 'Visita imminente di un antenato per richiamarti.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const feticcio: Move = {
  id: 'sciamano.feticcio',
  name: 'Feticcio',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: 'Quando allestisci un piccolo altare e pianti un feticcio',
  description:
    'Decidi come ti proteggerà:\n\n- se ti trovi entro distanza *lontano* dal feticcio puoi sentire e vedere come se ti trovassi al suo posto;\n- chi si *accampa* e dorme accanto al feticcio, recupera +1d8 PF.\n\nPuoi avere un solo *Feticcio* attivo alla volta.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const cioCheEStato: Move = {
  id: 'sciamano.cio-che-e-stato',
  name: 'Ciò che è Stato',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: 'Quando trascorri alcuni minuti a meditare sui resti di una creatura morta',
  description:
    'Hai una vaga percezione degli ultimi istanti della sua vita. Inoltre, se vuoi, puoi *discernere realtà* riguardo quel momento.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const amicoDegliSpiriti: Move = {
  id: 'sciamano.amico-degli-spiriti',
  name: 'Amico degli Spiriti',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    "Scegli una mossa non multiclasse dalla lista del Barbaro o del Druido. Se hai l'archetipo Elfo, puoi pescare anche dal Mago.",
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool Barbaro/Druido + Elfo→Mago (manuale r. 5506).
  multiclass: {
    picks: { kind: 'fixed-classes', classes: ['barbaro', 'druido'] },
    extendsByArchetype: [{ archetypeId: 'elfo', extraClasses: ['mago'] }],
  },
};

const bambolaVudu: Move = {
  id: 'sciamano.bambola-vudu',
  name: 'Bambola Vudù',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger:
    'Quando costruisci una bambola con le sembianze di qualcuno che hai incontrato e vi incorpori qualcosa di suo',
  description:
    'Tira+**SAG**. Con 7+, finché hai la bambola con te, sai sempre in che direzione si trova il tuo bersaglio. Con 7-9, il GM sceglie una limitazione:\n\n- funziona solo sotto la luce diretta della luna;\n- il bersaglio sa che tu possiedi la bambola e percepisce quando sei nelle vicinanze;\n- funziona solo se bagnata col sangue (tuo, e subisci 2 danni *ignora armatura*, o di qualcun altro).\n\nIl potere della bambola svanisce alla prossima luna nuova.',
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Sai sempre la direzione del bersaglio finché hai la bambola.' },
    on7to9: { text: 'Sai la direzione del bersaglio + il GM sceglie 1 limitazione.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const animaleGuida: Move = {
  id: 'sciamano.animale-guida',
  name: 'Animale Guida',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: 'Quando infliggi il colpo di grazia a un animale con le tue stesse mani',
  description:
    'Puoi prendere una Debilitazione a scelta e legare il suo spirito al tuo. Se lo fai, puoi sciogliere questo legame in qualsiasi momento e lo spirito ti mostrerà il percorso per arrivare dove hai bisogno di andare. Solo dopo aver sciolto il legame potrai curare normalmente la Debilitazione. Se hai già tutte le Debilitazioni segnate non puoi usare questa mossa.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const visitatoreSgradito: Move = {
  id: 'sciamano.visitatore-sgradito',
  name: 'Visitatore Sgradito',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger:
    "Quando tratti con qualcuno che, a ragione o per superstizione, teme il tuo legame con il mondo dell'aldilà",
  description: 'Puoi sempre usare la tua disponibilità ad andartene come leva per *parlamentare*.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const mistureDiSangue: Move = {
  id: 'sciamano.misture-di-sangue',
  name: 'Misture di Sangue',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger:
    'Quando hai il tempo e la tranquillità necessari, e usi il tuo sangue per creare una mistura',
  description:
    "Subisci 2 danni (*ignora armatura*) e scegli uno:\n\n- la mistura guarisce 2d4 PF;\n- la mistura ha l'etichetta *applicato* e rende il bersaglio una marionetta nelle tue mani per un breve lasso di tempo. Al termine dell'effetto la vittima saprà di essere stata controllata, ma non è detto che sappia da chi (a discrezione del GM);\n- la mistura garantisce +1 al prossimo tiro.\n\nTutte le misture hanno peso 0, 1 utilizzo, e non hanno alcun effetto su te stesso.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const sangueDalMioSangue: Move = {
  id: 'sciamano.sangue-dal-mio-sangue',
  name: 'Sangue dal Mio Sangue',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: 'Quando lasci che un alleato beva il tuo sangue',
  description:
    "Lancia un numero di d4 a tua scelta e subisci danni (*ignora armatura*) pari al risultato ottenuto. L'alleato lancia altrettanti d6, si cura di un numero di PF uguale al risultato ottenuto, e le sue emorragie vengono interrotte.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const guarigioneSpirituale: Move = {
  id: 'sciamano.guarigione-spirituale',
  name: 'Guarigione Spirituale',
  type: 'advanced_2_5',
  classId: 'sciamano',
  trigger: "Quando impieghi un'intera notte a curare l'anima di qualcuno (incluso te stesso)",
  description:
    "Tu non riesci a riposare, ma puoi rimuovere una delle Debilitazioni del bersaglio o una maledizione che lo affligge. Le maledizioni più deboli sono cancellate, quelle più potenti (a discrezione del GM) si manifestano di nuovo dopo qualche tempo.\n\nGli effetti di questa mossa non si cumulano con quelli di *Guarire l'Anima* del Chierico.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const sciamanoAdvanced2to5Moves: readonly Move[] = [
  bambolePendoli,
  ladroDOcchi,
  cheTuSia,
  spiritiGenerosi,
  animareZombi,
  collezionistaDiTeschi,
  canzoneDelMioPopolo,
  feticcio,
  cioCheEStato,
  amicoDegliSpiriti,
  bambolaVudu,
  animaleGuida,
  visitatoreSgradito,
  mistureDiSangue,
  sangueDalMioSangue,
  guarigioneSpirituale,
] as const;
